/**
 * siteStaticMap — build the query parameters for the `/api/static-map` server
 * route (J7) from a site boundary + KMZ layers.
 *
 * The server route assembles the actual Google Static Maps URL (host fixed,
 * key injected server-side); this helper produces ONLY the safe primitives —
 * center / zoom / size and the pre-encoded `path=` / `markers=` overlay spec
 * strings — so the boundary + layers render BAKED INTO the Google basemap
 * (Google owns the Web-Mercator projection, so the overlays pin to the tiles
 * exactly; no hand-rolled lat/lng→pixel math, unlike the SVG fallback).
 *
 * Site-layers pass (William QA 2026-07-02 "Site layers missing"):
 *   - Boundary rings ship as `enc:` ENCODED POLYLINES (Google's polyline
 *     algorithm, ~4–8 chars/vertex vs ~19 plain), colour-coded per ring via
 *     `ringColors` so multi-parcel sites match the site panel's palette.
 *   - The map frames to the boundary bbox (`args.bbox`, typically
 *     `site.siteBoundaryAggregate.bbox`, else derived from the rings); the
 *     red centre pin is kept ONLY when there are no boundary rings, so a
 *     boundary-bearing site never renders as a bare pin.
 *   - URL-length strategy: Google caps Static Maps URLs at ~8192 chars and
 *     the server route caps each `path`/`markers` spec at <2000 chars and 14
 *     entries. Rings are drawn FIRST (the primary ring is never dropped) and
 *     progressively downsampled until each spec + the running total fit the
 *     budgets; layer overlays fill the remaining slots.
 *
 * Shared by the report Site-Overview figure (StaticMapImage) AND the DOCX
 * site-overview embed, so the PDF/export map and the DOCX map stay
 * one-for-one from a single URL builder.
 */
import type { SiteLayer } from '../types/site';
import { colorForCategory } from '../config/siteLayerColors';

export interface StaticMapParams {
  center: string;
  zoom: string;
  size: string;
  scale: string;
  maptype: string;
  path: string[];
  markers: string[];
}

type LatLng = { lat: number; lng: number };
type Bbox = { south: number; west: number; north: number; east: number };

/** Google Static Maps URL ceiling is ~8192 chars; leave headroom for the
 *  host, base params and the server-injected key. Budgets the FORM-ENCODED
 *  length of the markers+path overlay params (the exact encoding the server's
 *  URLSearchParams sends upstream). */
const OVERLAY_CHAR_BUDGET = 7400;
/** Per-spec RAW cap — the server route drops any spec ≥ 2000 chars. */
const PATH_SPEC_CHAR_BUDGET = 1900;
/** The server route forwards at most 14 `path` / 14 `markers` specs. */
const MAX_PATH_SPECS = 14;
const MAX_MARKER_SPECS = 14;
/** Downsample ceilings — enc: polylines are compact, so rings afford more
 *  vertices than the old plain-text 60 while producing SHORTER specs. */
const RING_MAX_POINTS = 100;
const LAYER_LINE_MAX_POINTS = 60;
/** Ring fill alpha (hex) — ~15%, matching the legacy amber 0xD9770626 fill. */
const RING_FILL_ALPHA = '26';

function isFinitePt(p: LatLng | undefined): p is LatLng {
  return !!p && Number.isFinite(p.lat) && Number.isFinite(p.lng);
}

function fmtPt(p: LatLng): string {
  return `${p.lat.toFixed(5)},${p.lng.toFixed(5)}`;
}

/** Even-stride downsample so the assembled URL stays under Google's ceiling. */
function downsample<T>(coords: readonly T[], max: number): T[] {
  if (coords.length <= max) return [...coords];
  const out: T[] = [];
  const step = (coords.length - 1) / (max - 1);
  for (let i = 0; i < max; i++) out.push(coords[Math.round(i * step)]);
  return out;
}

/** A 6-hex colour → bare lowercase hex6, else the fallback hex6. */
function hex6(color: string | undefined, fallbackHex6: string): string {
  if (color) {
    const m = color.match(/^#?([0-9a-fA-F]{6})$/);
    if (m) return m[1].toLowerCase();
  }
  return fallbackHex6.toLowerCase();
}

/** A 6-hex colour → Static-Maps `0xRRGGBB`, else the fallback hex6. */
function staticMapColor(color: string | undefined, fallbackHex6: string): string {
  return `0x${hex6(color, fallbackHex6)}`;
}

/* ── Google Encoded Polyline Algorithm Format ─────────────────────────────
 * https://developers.google.com/maps/documentation/utilities/polylinealgorithm
 * 1e-5 fixed point, delta-encoded, zig-zag signed, 5-bit groups + 63 offset.
 * Implemented locally (tiny, public algorithm) — no external dependency. */

function encodeSignedDelta(v: number): string {
  let out = '';
  // Zig-zag: left-shift, invert when negative so the sign bit is the LSB.
  let u = v < 0 ? ~(v << 1) : v << 1;
  while (u >= 0x20) {
    out += String.fromCharCode((0x20 | (u & 0x1f)) + 63);
    u >>= 5;
  }
  return out + String.fromCharCode(u + 63);
}

/**
 * Encode points as a Google polyline (the payload of a `path=…|enc:` spec).
 * Emits ASCII 63–126 only, so it round-trips cleanly through URL encoding.
 */
export function encodePolyline(points: ReadonlyArray<LatLng>): string {
  let out = '';
  let prevLat = 0;
  let prevLng = 0;
  for (const p of points) {
    const lat = Math.round(p.lat * 1e5);
    const lng = Math.round(p.lng * 1e5);
    out += encodeSignedDelta(lat - prevLat) + encodeSignedDelta(lng - prevLng);
    prevLat = lat;
    prevLng = lng;
  }
  return out;
}

/** Exact form-encoded wire length of one `key=value` query pair (+ '&'). The
 *  server assembles the upstream URL with URLSearchParams, so measuring with
 *  URLSearchParams here budgets the SAME bytes Google will see. */
function formEncodedLen(key: string, value: string): number {
  return new URLSearchParams([[key, value]]).toString().length + 1;
}

/**
 * Derive a zoom level (1–20) that fits a lat/lng span in a tile size. Uses the
 * Web-Mercator world-tile math Google's Static Maps uses internally. Falls back
 * to a sensible site-scale zoom (14) when the span is degenerate.
 */
export function fitZoom(
  bbox: Bbox,
  sizePx: { w: number; h: number },
  paddingFraction = 0.2,
): number {
  const WORLD = 256;
  const ZOOM_MAX = 20;
  const latSpan = Math.max(bbox.north - bbox.south, 1e-9);
  const lngSpan = Math.max(bbox.east - bbox.west, 1e-9);
  if (!Number.isFinite(latSpan) || !Number.isFinite(lngSpan)) return 14;

  const latRad = (deg: number) => {
    const sin = Math.sin((deg * Math.PI) / 180);
    const y = Math.log((1 + sin) / (1 - sin)) / 2;
    return Math.max(Math.min(y, Math.PI), -Math.PI) / 2;
  };
  const latFraction = (latRad(bbox.north) - latRad(bbox.south)) / Math.PI;
  const lngFraction = lngSpan / 360;
  const pad = 1 + Math.max(0, paddingFraction) * 2;
  const latZoom = Math.log2(sizePx.h / WORLD / Math.max(latFraction, 1e-9) / pad);
  const lngZoom = Math.log2(sizePx.w / WORLD / Math.max(lngFraction, 1e-9) / pad);
  const z = Math.floor(Math.min(latZoom, lngZoom));
  if (!Number.isFinite(z)) return 14;
  return Math.max(1, Math.min(ZOOM_MAX, z));
}

export interface BuildStaticMapArgs {
  /** Center fallback (site point) when no boundary bbox resolves. */
  center: LatLng;
  /** Site boundary (primary parcel) polygon — never dropped by the budget. */
  boundary?: ReadonlyArray<LatLng>;
  /** Optional secondary parcel rings (multi-parcel sites), drawn in order. */
  extraRings?: ReadonlyArray<ReadonlyArray<LatLng>>;
  /**
   * Per-ring stroke colours (`#RRGGBB` / bare hex6), indexed by ring position
   * (boundary = 0, extraRings[i] = i + 1), wrapping modulo the array. Pass the
   * site panel's parcel palette for colour-coded multi-parcel parity. Absent ⇒
   * every ring keeps the legacy amber #D97706 (viewer BoundaryPolygon parity).
   */
  ringColors?: ReadonlyArray<string>;
  /**
   * Authoritative frame — typically `site.siteBoundaryAggregate.bbox`. When
   * present (and finite) the map centers/zooms to IT; absent, the frame is
   * derived from the ring vertices; with neither, the centre pin at zoom 14.
   */
  bbox?: Bbox;
  /** KMZ layers → coloured markers (points) / paths (lines, polygons). */
  layers?: ReadonlyArray<SiteLayer>;
  /** Output pixel box (logical px; scale doubles it). Default 640x320. */
  size?: { w: number; h: number };
  scale?: 1 | 2;
  maptype?: 'roadmap' | 'satellite' | 'hybrid' | 'terrain';
}

/** Encode one ring as a `path=` spec, progressively downsampling until the
 *  raw spec and its wire length fit the given budgets. Null when even the
 *  8-point floor cannot fit (caller drops the ring). */
function encodeRingSpec(
  ring: ReadonlyArray<LatLng>,
  strokeHex6: string,
  remainingBudget: number,
): string | null {
  const floor = Math.min(8, ring.length);
  let pts = Math.min(ring.length, RING_MAX_POINTS);
  for (;;) {
    const spec =
      `color:0x${strokeHex6}ff|weight:2|fillcolor:0x${strokeHex6}${RING_FILL_ALPHA}` +
      `|enc:${encodePolyline(downsample(ring, pts))}`;
    if (spec.length <= PATH_SPEC_CHAR_BUDGET && formEncodedLen('path', spec) <= remainingBudget) {
      return spec;
    }
    if (pts <= floor) return null;
    pts = Math.max(floor, Math.floor(pts / 2));
  }
}

/**
 * Build the `/api/static-map` query params: boundary rings (colour-coded,
 * `enc:` polylines) + per-layer overlays, framed to the boundary bbox; the
 * red centre pin ONLY when no boundary ring resolves (bare-pin sites keep
 * their exact legacy params). Returns `null` when there is no usable center
 * (caller falls back to the SVG diagram).
 */
export function buildStaticMapParams(args: BuildStaticMapArgs): StaticMapParams | null {
  const { center, boundary, extraRings, layers, ringColors } = args;
  if (!isFinitePt(center)) return null;
  const size = args.size ?? { w: 640, h: 320 };
  const sizeStr = `${size.w}x${size.h}`;

  // Drawable rings — primary boundary first, then the extra parcels. A ring
  // needs ≥ 3 finite vertices (Static Maps auto-closes the polygon).
  const rings: LatLng[][] = [];
  const collectRing = (ring?: ReadonlyArray<LatLng>) => {
    const r = (ring ?? []).filter(isFinitePt);
    if (r.length >= 3) rings.push(r);
  };
  collectRing(boundary);
  for (const ring of extraRings ?? []) collectRing(ring);

  // Frame — the explicit aggregate bbox wins; else the union of ring
  // vertices; else the centre point + a site-scale zoom.
  const bboxFinite = (b?: Bbox): b is Bbox =>
    !!b && [b.south, b.west, b.north, b.east].every(Number.isFinite);
  let frame: Bbox | null = bboxFinite(args.bbox) ? args.bbox : null;
  if (!frame && rings.length > 0) {
    const pts = rings.flat();
    frame = {
      south: Math.min(...pts.map((p) => p.lat)),
      north: Math.max(...pts.map((p) => p.lat)),
      west: Math.min(...pts.map((p) => p.lng)),
      east: Math.max(...pts.map((p) => p.lng)),
    };
  }
  let centerStr = fmtPt(center);
  let zoom = 14;
  if (frame) {
    centerStr = `${((frame.south + frame.north) / 2).toFixed(5)},${((frame.west + frame.east) / 2).toFixed(5)}`;
    zoom = fitZoom(frame, size);
  }

  // Overlay budget — bounded by what the server forwards + Google's URL cap.
  let budget = OVERLAY_CHAR_BUDGET;
  const path: string[] = [];
  const markers: string[] = [];

  // Red centre pin ONLY when no boundary ring resolves — a boundary-bearing
  // site is marked by its rings, never a bare pin on top of them.
  if (rings.length === 0) {
    const pin = `color:red|${fmtPt(center)}`;
    markers.push(pin);
    budget -= formEncodedLen('markers', pin);
  }

  // Boundary rings FIRST (colour-coded per parcel; the primary ring can only
  // shrink, never drop — encodeRingSpec bottoms out at 8 vertices).
  rings.forEach((ring, i) => {
    if (path.length >= MAX_PATH_SPECS) return;
    const stroke = ringColors && ringColors.length > 0
      ? hex6(ringColors[i % ringColors.length], 'd97706')
      // Legacy default — amber #D97706, matching the viewer's BoundaryPolygon.
      : 'D97706';
    const spec = encodeRingSpec(ring, stroke, budget);
    if (!spec) return;
    path.push(spec);
    budget -= formEncodedLen('path', spec);
  });

  // Layer overlays — point → markers, line/polygon → path. Capped, budgeted
  // and downsampled; layers only take the slots the rings left over.
  let drawn = 0;
  for (const layer of (layers ?? []).filter((l) => l && l.visible !== false)) {
    if (drawn >= 12 || budget <= 0) break;
    const coords = (layer.geometry?.coordinates ?? []).filter(isFinitePt);
    if (coords.length === 0) continue;
    const color = staticMapColor(colorForCategory(layer.category, layer.color), '94a3b8');
    if (layer.geometry.type === 'point') {
      if (markers.length >= MAX_MARKER_SPECS) continue;
      const pts = downsample(coords, 8).map(fmtPt).join('|');
      const spec = `color:${color}|size:small|${pts}`;
      if (formEncodedLen('markers', spec) > budget) continue;
      markers.push(spec);
      budget -= formEncodedLen('markers', spec);
      drawn++;
    } else if (coords.length >= 2) {
      if (path.length >= MAX_PATH_SPECS) continue;
      const weight = layer.geometry.type === 'polygon'
        ? `weight:2|fillcolor:${color}1a|`
        : 'weight:3|';
      const spec = `color:${color}|${weight}enc:${encodePolyline(downsample(coords, LAYER_LINE_MAX_POINTS))}`;
      if (spec.length > PATH_SPEC_CHAR_BUDGET || formEncodedLen('path', spec) > budget) continue;
      path.push(spec);
      budget -= formEncodedLen('path', spec);
      drawn++;
    }
  }

  return {
    center: centerStr,
    zoom: String(zoom),
    size: sizeStr,
    scale: String(args.scale ?? 2),
    maptype: args.maptype ?? 'roadmap',
    path,
    markers,
  };
}

/** Encode {@link StaticMapParams} into a `/api/static-map?…` query string. */
export function staticMapQuery(params: StaticMapParams, extra?: Record<string, string>): string {
  const q = new URLSearchParams();
  q.set('center', params.center);
  q.set('zoom', params.zoom);
  q.set('size', params.size);
  q.set('scale', params.scale);
  q.set('maptype', params.maptype);
  for (const p of params.path) q.append('path', p);
  for (const m of params.markers) q.append('markers', m);
  for (const [k, v] of Object.entries(extra ?? {})) {
    if (v) q.set(k, v);
  }
  return q.toString();
}
